/**
 * 評価を上げるために（PI-1）— 仕様書§07 / 説明文DB DB07。
 *
 * 【手順（仕様書§07）】
 *  1. 最も低いスコアを選ぶ
 *  2. その中で最も低い変数を選ぶ
 *  3. その変数に対応する構造（部署）を見る
 *  4. 全社の余力で打ち手の型を決める
 *
 * 【余力について（重要）】
 *  仕様書は「余力2問」を前提にしているが、現行の74問に該当する設問は無い。
 *  Part3 の SK-01〜04 は論理性（データ駆動判断・比較分析・定量振り返り・論理的説明）で、
 *  負荷を測る設問ではない。よって手順4のゲートは今は働かない。
 *  設問が追加されたら slack を渡すだけで有効になるようにしてある。
 *
 * 【目安値の出し方】
 *  DBは「目標値＝現在＋改善見込み（類似企業の傾向に基づく推計）」としか書いておらず、
 *  実データも無い。ここでは「最も低い変数を次のグレードの境目まで上げたら
 *  他がそのままなら全体はどうなるか」を自分たちの式で計算し、目安として出す。
 *  仕様書の例（相談先の広がり 2.7 → 3.4）は、C→B の境目 3.40 と一致する。
 *  ただし同じ例のつながり 51→62 は式から再現できないため、こちらは採らず再計算する。
 */
import {
  CONNECTION_VAR_KEYS,
  CONNECTION_VAR_LABEL,
  aggregateConnectionV3,
  toGrade,
  toScore100,
  type ConnectionVarsV3,
  type Grade,
  type OrgScoresV3,
  type ScoreKey,
} from './orgScoreV3';
import { findOrgText, type OrgTextEntry } from './orgTextsV3';
import type { ConsultDeptResultV3 } from './orgConsultV3';
import type { OrgCohesionV3 } from './orgCohesionV3';

/** 余力の閾値。これ未満は「増やす」系を出さない（07-余力-条件）。 */
export const SLACK_LOW = 3.0;

/** グレードの境目（1.00〜5.00スケール）。次の段へ上げる目安に使う。 */
const GRADE_EDGES = [2.6, 3.4, 4.2];

/** 現在値の次のグレード境目。すでに最上位なら +0.4 とする。 */
export function nextGradeEdge(v: number): number {
  const next = GRADE_EDGES.find((e) => e > v + 1e-9);
  return next ?? Math.min(5, v + 0.4);
}

/** PI-1 のテーマ。DB07 のIDと対応させる。 */
export type PiTheme =
  | '集中解消'
  | '役割権限'
  | '橋渡し'
  | '管理職'
  | '人物評価'
  | '採用配置'
  | 'KPI';

/** 追加型（現場に何かを足す）のテーマ。余力が低いときは出さない。 */
const ADDITIVE: PiTheme[] = ['橋渡し', 'KPI', '人物評価', '採用配置'];

/** つながりの4変数から選ぶテーマ。 */
const CONN_THEME: Record<keyof ConnectionVarsV3, PiTheme> = {
  consultReach: '集中解消', // 相談先の広がり → 1人に集まっている判断を渡す
  crossDept: '橋渡し', // 部署間のつながり → 部署をまたぐ仕事に担当を出す
  teamHelp: '管理職', // チーム内の助け合い → 管理職の余白をつくる
  workSharing: '役割権限', // 仕事の共有度 → 確認なしで決めてよいことを決める
};

/** 噛み合わせ(DB03)のテーマから PI テーマへの対応。 */
const COHESION_THEME: Record<string, PiTheme> = {
  '役割・対話の設計': '役割権限',
  部門連携: '橋渡し',
  '採用・受入れ': '採用配置',
};

export interface ImprovementChainStep {
  label: string;
  now: string;
  target: string;
}

/**
 * この打ち手をやり切ったときの GB組織スコアの見通し。
 *
 * 「最も低い項目が次の段階まで上がったら、他がそのままなら全体はどうなるか」を
 * 計算したもの。連鎖(chain)の最後の行と同じ値を、数値のまま持つ。
 */
export interface GbScoreOutlook {
  now: number;
  target: number;
  nowGrade: Grade;
  targetGrade: Grade;
}

export interface OrgImprovementV3 {
  theme: PiTheme;
  /** DB07 の取り組み・理由・指標 */
  text?: OrgTextEntry;
  /** 起点になった最低スコア */
  weakest: ScoreKey;
  /** つながりが最低のときの最低変数 */
  weakestVar?: keyof ConnectionVarsV3;
  /** その変数に関係する部署（つながりの構造から）。無ければ null */
  focusDept: ConsultDeptResultV3 | null;
  /** 連鎖（現在 → 目安） */
  chain: ImprovementChainStep[];
  /**
   * 具体的な進め方。起点になった部署の形（DB05）の次の一手。
   * 起点の部署が無いときは空文字。
   * PC版・モバイル版の両方が使うので、ここで1度だけ組み立てる。
   */
  howTo: string;
  /** GB組織スコアの見通し（現状 → 期待値） */
  gbOutlook: GbScoreOutlook;
  /** 余力が低いときの注記 */
  slackNote?: OrgTextEntry;
  /** 余力が取得できたか（設問が無いうちは false） */
  hasSlack: boolean;
}

export interface BuildImprovementInput {
  scores: OrgScoresV3;
  consult: ConsultDeptResultV3[];
  cohesion: OrgCohesionV3;
  /** 全社の余力の中央値（1.00〜5.00）。設問が無いうちは undefined */
  slackMedian?: number;
}

/** PI-1 を決めて、連鎖つきの改善提案を組み立てる。 */
export function buildOrgImprovementV3(input: BuildImprovementInput): OrgImprovementV3 {
  const { scores, consult, cohesion, slackMedian } = input;
  const hasSlack = typeof slackMedian === 'number';
  const lowSlack = hasSlack && slackMedian < SLACK_LOW;

  // --- 手順1・2: 最低スコア → その中の最低変数 ---
  const weakest = scores.weakest;
  const weakestVar = weakest === 'connection' ? scores.connection.weakestVar : undefined;

  // --- テーマを選ぶ ---
  let theme: PiTheme;
  if (weakestVar) {
    theme = CONN_THEME[weakestVar];
  } else if (weakest === 'wellbeing') {
    theme = '役割権限';
  } else {
    // 離職の兆し。噛み合わせのテーマがあればそれを優先する
    theme = COHESION_THEME[cohesion.text?.テーマ ?? ''] ?? '人物評価';
  }
  // 余力が低いときは追加型を出さない（07-余力-条件）
  if (lowSlack && ADDITIVE.includes(theme)) theme = '集中解消';

  // --- 手順3: その変数に関係する部署 ---
  // 一極集中・集まりぎみの部署のうち、最も集中している1つを見る。
  const focusDept =
    consult
      .filter((d) => d.shape === '一極集中' || d.shape === '集まりぎみ')
      .sort((a, b) => b.maxRate - a.maxRate)[0] ?? null;

  const chain = buildChain(scores, weakestVar);

  return {
    theme,
    text: findOrgText('DB07', `07-${theme}`),
    weakest,
    weakestVar,
    focusDept,
    chain: chain.steps,
    gbOutlook: chain.outlook,
    howTo: buildHowTo(focusDept),
    slackNote: lowSlack ? findOrgText('DB07', '07-余力-注記') : undefined,
    hasSlack,
  };
}

/** つながりの形状 → DB05 のエントリID。 */
const CONSULT_SHAPE_TEXT_ID: Record<string, string> = {
  分断: '05-分断',
  一極集中: '05-一極',
  集まりぎみ: '05-V',
  助け合い: '05-分散',
  希薄: '05-希薄',
};

/** 起点の部署の形から「具体的な進め方」を組み立てる（{氏名} は実名に差し替える）。 */
function buildHowTo(focusDept: ConsultDeptResultV3 | null): string {
  if (!focusDept?.shape) return '';
  const t = findOrgText('DB05', CONSULT_SHAPE_TEXT_ID[focusDept.shape]);
  return (t?.次の一手 ?? '').replace(/\{氏名\}/g, focusDept.topName ?? 'その方');
}

/**
 * 連鎖（変数 → スコア → GBスコア）を、自分たちの式で再計算して出す。
 * つながりが最低でないときは、そのスコア自体の目安だけを出す。
 */
function buildChain(
  scores: OrgScoresV3,
  weakestVar?: keyof ConnectionVarsV3
): { steps: ImprovementChainStep[]; outlook: GbScoreOutlook } {
  const steps: ImprovementChainStep[] = [];
  const fmt2 = (v: number) => v.toFixed(2);
  /** 連鎖の最後の行（GB組織スコア）を積みつつ、同じ値を数値でも返す。 */
  const finish = (nextGb: number, nextLowest: number) => {
    const targetGrade = toGrade(nextLowest);
    steps.push({
      label: 'GB組織スコア',
      now: `${scores.gbScore}点（評価 ${scores.grade}）`,
      target: `${nextGb}点（評価 ${targetGrade}）`,
    });
    return {
      steps,
      outlook: {
        now: scores.gbScore,
        target: nextGb,
        nowGrade: scores.grade,
        targetGrade,
      },
    };
  };

  if (weakestVar) {
    const now = scores.connection.vars[weakestVar];
    const target = nextGradeEdge(now);
    const nextVars: ConnectionVarsV3 = { ...scores.connection.vars, [weakestVar]: target };
    const nextConn = aggregateConnectionV3(nextVars);
    steps.push({
      label: CONNECTION_VAR_LABEL[weakestVar],
      now: fmt2(now),
      target: fmt2(target),
    });
    steps.push({
      label: 'つながり',
      now: `${scores.connection.score}点`,
      target: `${nextConn.score}点`,
    });
    const nextGb = Math.round(
      (scores.wellbeing.score + nextConn.score + scores.retention.score) / 3
    );
    const nextLowest = Math.min(scores.wellbeing.score, nextConn.score, scores.retention.score);
    return finish(nextGb, nextLowest);
  }

  // つながり以外が最低のとき: そのスコアを次のグレードの境目まで上げた場合
  const cur = scores.weakest === 'wellbeing' ? scores.wellbeing : scores.retention;
  const targetAdj = nextGradeEdge(cur.adjusted);
  const targetScore = toScore100(targetAdj);
  const label = scores.weakest === 'wellbeing' ? '働きがい' : '離職の兆し';
  const others =
    scores.weakest === 'wellbeing'
      ? [scores.connection.score, scores.retention.score]
      : [scores.wellbeing.score, scores.connection.score];
  const nextGb = Math.round((targetScore + others[0] + others[1]) / 3);
  const nextLowest = Math.min(targetScore, ...others);
  steps.push({ label, now: `${cur.score}点`, target: `${targetScore}点` });
  return finish(nextGb, nextLowest);
}

/** 表示用: つながり4変数を低い順に並べる。 */
export function connVarsSorted(vars: ConnectionVarsV3) {
  return CONNECTION_VAR_KEYS.map((k) => ({ key: k, value: vars[k] })).sort(
    (a, b) => a.value - b.value
  );
}
